import { useEffect, useState } from "react";
import { ZoomIn } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Lightbox } from "@/components/site/Lightbox";
import { useSiteImages } from "@/hooks/useSiteImages";
import { PHOTO_CATEGORIES } from "@/config";

const imgSrc = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const ALL = "all";
const STUDIO = "studio";

export const Gallery = () => {
  const [items, setItems] = useState([]); // {url, cat, catLabel}
  const [filter, setFilter] = useState("artificial-plants");
  const [lightbox, setLightbox] = useState({ index: null });

  const data = useSiteImages();

  useEffect(() => {
    if (!data) return;
    const list = [];
    (data.gallery || []).forEach((url) => list.push({ url, cat: STUDIO, catLabel: "Studio" }));
    PHOTO_CATEGORIES.forEach((c) => {
      (data.categories?.[c.id] || []).forEach((url) =>
        list.push({ url, cat: c.id, catLabel: c.label })
      );
    });
    setItems(list);
  }, [data]);

  const filtered = filter === ALL ? items : items.filter((p) => p.cat === filter);

  if (items.length === 0) return null;

  const availableCats = [{ id: STUDIO, label: "Studio Gallery" }, ...PHOTO_CATEGORIES];

  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 bg-white border-b border-line">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div className="max-w-xl">
            <span className="label-caps text-forest block mb-2">Inside Room 9</span>
            <h2 data-testid="gallery-heading" className="heavy-heading text-3xl sm:text-4xl font-extrabold text-ink">
              Studio Gallery
            </h2>
            <p className="text-sm text-muted-g mt-2">
              Real photos of the space, props and backdrops. Pick a category and click any
              photo to expand.
            </p>
          </div>
          <div className="shrink-0">
            <label className="label-caps text-muted-g block mb-2">View by category</label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger
                data-testid="gallery-category-select"
                className="w-56 rounded-lg border-line-2 bg-white text-sm font-semibold text-ink"
              >
                <SelectValue placeholder="All photos" />
              </SelectTrigger>
              <SelectContent className="rounded-lg border-line-2">
                <SelectItem value={ALL} data-testid="gallery-category-option-all">
                  All photos ({items.length})
                </SelectItem>
                {availableCats.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`gallery-category-option-${c.id}`}>
                    {c.label} ({items.filter((p) => p.cat === c.id).length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Photo grid — all photos visible as cards, no cropping */}
        {filtered.length === 0 ? (
          <div
            data-testid="gallery-empty"
            className="rounded-xl border border-dashed border-line-2 bg-bg h-48 flex items-center justify-center"
          >
            <p className="text-sm text-muted-g">No photos in this category yet — check back soon.</p>
          </div>
        ) : (
          <div data-testid="gallery-grid" className="columns-2 md:columns-3 lg:columns-4 gap-4">
            {filtered.map((p, i) => (
              <button
                key={p.url + i}
                type="button"
                onClick={() => setLightbox({ index: i })}
                data-testid={`gallery-card-${i}`}
                aria-label={`Expand ${p.catLabel} photo ${i + 1}`}
                className="relative group block w-full mb-4 break-inside-avoid rounded-xl overflow-hidden border border-line-2 bg-[#171512] cursor-zoom-in"
              >
                <img
                  src={imgSrc(p.url)}
                  alt={`${p.catLabel} — Room 9 photo ${i + 1}`}
                  loading="lazy"
                  className="w-full h-auto block group-hover:scale-105 transition-transform duration-500"
                />
                <span className="absolute top-3 left-3 bg-ink/70 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded">
                  {p.catLabel}
                </span>
                <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/25 transition-colors duration-300 flex items-center justify-center">
                  <ZoomIn size={22} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <Lightbox
        photos={filtered.map((p) => p.url)}
        index={lightbox.index}
        onClose={() => setLightbox({ index: null })}
        onStep={(d) =>
          setLightbox((s) => ({ index: (s.index + d + filtered.length) % filtered.length }))
        }
      />
    </section>
  );
};
